import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Subscription} from 'rxjs';
import {StocksService} from '../../core/services/gestion-stock/stocks.service';
import {UtilsService} from '../../core/utils/utils.service';
import {ButtonThemeEnum} from '../../shared/ui/button/button-theme-enum';
import {ToastService} from '../../core/services/technique/toast.service';
import {MSG_KEY, MSG_SEVERITY, SECURITY} from '../../core/constants';
import {DxDataGridComponent} from 'devextreme-angular';
import {confirm} from 'devextreme/ui/dialog';
import {ResponseWrapper} from '../../core/suppliers/wrappers/response-wrapper';
import {Auth2Service} from '../../core/services/security/auth2.service';
import {IconThemeEnum} from '../../shared/ui/icon/icon-theme-enum';
import {InternationalizationService} from '../../core/services/i8n/i8n.service';

@Component({
  selector: 'yo-validation-inventaire',
  templateUrl: './validation-inventaire.component.html',
  styleUrls: ['./validation-inventaire.component.scss'],
  standalone: false
})
export class ValidationInventaireComponent implements OnInit, OnDestroy {

  public readonly ButtonThemeEnum = ButtonThemeEnum;
  public readonly IconThemeEnum = IconThemeEnum;

  @ViewChild('grid') grid: DxDataGridComponent;

  validations: any[] = [];
  selectedValidationList: any[] = [];
  displayCreateDialog = false;
  errors: { infosLine: string }[] = [];
  displayDeletePopupErrors = false;

  hasNiv1 = false;
  hasNiv2 = false;
  hasNiv3 = false;
  maxNiveauUser = 0;

  displayInfoAnnexeDialog = false;
  editingValidation: any = null;
  infoAnnexeValue = '';
  infoAnnexeReadOnly = false;

  readonly validationFilterList = [
    {text: 'A valider', value: 'validable'},
    {text: 'Validé', value: 'valide'},
    {text: 'Non validé', value: 'non_valide'},
  ];

  getValidationFilterValue = (rowData: any): string => {
    const niveauAtteint = rowData.validationNiv1Par
      ? (rowData.validationNiv2Par ? (rowData.validationNiv3Par ? 3 : 2) : 1)
      : 0;
    if (this.maxNiveauUser > 0 && niveauAtteint >= this.maxNiveauUser) return 'valide';
    const validable = (this.hasNiv1 && !rowData.validationNiv1Par)
      || (this.hasNiv2 && rowData.validationNiv1Par && !rowData.validationNiv2Par)
      || (this.hasNiv3 && rowData.validationNiv2Par && !rowData.validationNiv3Par);
    return validable ? 'validable' : 'non_valide';
  };

  availableHistoryList: any[] = [];
  udpList: any[] = [];
  selectedUdpId: number = null;
  monthList: any[] = [];
  selectedHistoryId: number = null;

  private sub: Subscription;

  constructor(private stockSvc: StocksService,
              private toastSvc: ToastService,
              private auth2Svc: Auth2Service,
              private i8nSvc: InternationalizationService,
              public utils: UtilsService) {
  }

  ngOnInit(): void {
    this.loadValidations();
    const roles = this.auth2Svc.utilisateur?.roles ?? [];
    this.hasNiv1 = roles.some(r => r === SECURITY.ROLES.ROLE_VALIDATION_HISTORIQUE_FIN_MOIS_NIV_1);
    this.hasNiv2 = roles.some(r => r === SECURITY.ROLES.ROLE_VALIDATION_HISTORIQUE_FIN_MOIS_NIV_2);
    this.hasNiv3 = roles.some(r => r === SECURITY.ROLES.ROLE_VALIDATION_HISTORIQUE_FIN_MOIS_NIV_3);
    this.maxNiveauUser = this.hasNiv3 ? 3 : this.hasNiv2 ? 2 : this.hasNiv1 ? 1 : 0;
  }

  loadValidations(): void {
    this.sub = this.stockSvc.getValidationsInventaire().subscribe(res => {
      this.validations = res.resultList;
    });
  }

  onCreate(): void {
    this.stockSvc.getAvailableInventaireHistory().subscribe(res => {
      this.availableHistoryList = res.resultList || [];
      const udpMap = new Map<number, string>();
      this.availableHistoryList.forEach(item => {
        if (!udpMap.has(item.uniteProductionId)) {
          udpMap.set(item.uniteProductionId, item.uniteProductionLibelle);
        }
      });
      this.udpList = Array.from(udpMap, ([id, libelle]) => ({id, libelle}));
      this.selectedUdpId = null;
      this.monthList = [];
      this.selectedHistoryId = null;
      this.displayCreateDialog = true;
    });
  }

  onUdpChange(udpId: number): void {
    this.selectedUdpId = udpId;
    this.monthList = this.availableHistoryList
      .filter(item => item.uniteProductionId === udpId)
      .map(item => ({
        inventaireHistoryId: item.inventaireHistoryId,
        inventaireDate: item.inventaireDate,
        label: this.formatMonth(item.inventaireDate)
      }));
    this.selectedHistoryId = null;
  }

  onSelectionChanged(event: any): void {
    this.selectedValidationList = event.selectedRowsData;
  }

  onApprouveSelection(): void {
    const idList = this.selectedValidationList.map(v => v.inventaireHistoryId);
    this.stockSvc.approuveInventaire(idList).subscribe(() => {
      this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, `Validation effectuée avec succès`);
      this.selectedValidationList = [];
      this.grid.instance.clearSelection();
      this.loadValidations();
    });
  }

  onInvalideSelection(): void {
    const idList = this.selectedValidationList.map(v => v.inventaireHistoryId);
    this.stockSvc.invalideInventaire(idList).subscribe(() => {
      this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, `Invalidation effectuée avec succès`);
      this.selectedValidationList = [];
      this.grid.instance.clearSelection();
      this.loadValidations();
    });
  }

  get canDeleteSelection(): boolean {
    return this.selectedValidationList.length > 0 && this.selectedValidationList.some(row => !this.isValidated(row));
  }

  get canApprouveSelection(): boolean {
    return this.selectedValidationList.length > 0 && this.selectedValidationList.some(row => this.canValidate(row));
  }

  get canInvalideSelection(): boolean {
    return this.selectedValidationList.length > 0 && this.selectedValidationList.some(row => this.canInvalidate(row));
  }

  private canInvalidate(row: any): boolean {
    if (this.hasNiv3 && row.validationNiv3Par) return true;
    if (this.hasNiv2 && row.validationNiv2Par && (!row.validationNiv3Par || this.hasNiv3)) return true;
    if (this.hasNiv1 && row.validationNiv1Par && (!row.validationNiv2Par || (this.hasNiv2 && (!row.validationNiv3Par || this.hasNiv3)))) return true;
    return false;
  }

  onSave(): void {
    if (!this.selectedHistoryId) return;
    this.stockSvc.saveValidationInventaire(this.selectedHistoryId).subscribe(() => {
      this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, `La validation de fin de mois a été créée avec succès`);
      this.displayCreateDialog = false;
      this.loadValidations();
    });
  }

  async deleteSelected(): Promise<void> {
    const selected: any[] = await this.grid.instance.getSelectedRowsData();
    if (!selected || !selected.length) return;
    const confirmed = await confirm(
      selected.length > 1
        ? `Êtes-vous sûr de vouloir supprimer les ${selected.length} validations sélectionnées ?`
        : 'Êtes-vous sûr de vouloir supprimer cette validation ?',
      'Suppression'
    );
    if (!confirmed) return;
    const ids: number[] = selected.map(row => row.inventaireHistoryId);
    this.stockSvc.deleteValidationInventaire(ids).subscribe(response => {
      const res = (response as ResponseWrapper<any>).one;
      const deletedCount = ids.length - (res?.messagesErrorList?.length ?? 0);
      if (deletedCount > 0) {
        this.loadValidations();
        this.grid.instance.clearSelection();
        this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS,
          deletedCount > 1
            ? `${deletedCount} validations supprimées avec succès.`
            : 'Validation supprimée avec succès.');
      }
      if (res?.messagesErrorList?.length > 0) {
        this.errors = res.messagesErrorList.map((error: any) => ({
          infosLine: this.i8nSvc.getLabelFromCode(error.code, error.args ? [error.args] : [])
        }));
        this.displayDeletePopupErrors = true;
      }
    });
  }

  isValidated(row: any): boolean {
    return !!row.validationNiv1Par;
  }

  isEditable(row: any): boolean {
    return !this.isValidated(row) && (this.hasNiv1 || this.hasNiv2 || this.hasNiv3);
  }

  onInfoAnnexeCellClick(event: any): void {
    if (event.column?.dataField === 'infoAnnexe' && event.row?.rowType === 'data' && event.row.data.infoAnnexe) {
      this.openInfoAnnexeDialog(event.row.data);
    }
  }

  onCellPrepared(event: any): void {
    if (event.column?.dataField === 'infoAnnexe' && event.rowType === 'data' && event.data.infoAnnexe) {
      event.cellElement.classList.add('pointer');
      if (this.isEditable(event.data)) {
        event.cellElement.classList.add('editable-background-color');
      }
    }
  }

  openInfoAnnexeDialog(row: any): void {
    if (!this.isEditable(row) && !this.isValidated(row)) return;
    this.editingValidation = row;
    this.infoAnnexeValue = row.infoAnnexe ?? '';
    this.infoAnnexeReadOnly = !this.isEditable(row);
    this.displayInfoAnnexeDialog = true;
  }

  onSaveInfoAnnexe(): void {
    if (!this.editingValidation || !this.isEditable(this.editingValidation)) return;
    this.stockSvc.updateInfoAnnexeValidationInventaire(
      this.editingValidation.inventaireHistoryId, this.infoAnnexeValue
    ).subscribe(() => {
      this.editingValidation.infoAnnexe = this.infoAnnexeValue;
      this.displayInfoAnnexeDialog = false;
      this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, 'Info annexe enregistrée.');
    });
  }

  getFirstLine(text: string): string {
    var index = text.indexOf("</p>");
    return text.substring(0, index === -1 ? undefined : index).replace(/<[^>]*>/g, '');
  }

  closeErrors(): void {
    this.errors = [];
    this.displayDeletePopupErrors = false;
  }

  getValidationIcon(row: any): string {
    return row.validationNiv1Par ? 'circle-check' : 'circle';
  }

  getValidationTheme(row: any, niveau: number): IconThemeEnum {
    if (row[`validationNiv${niveau}Par`]) return IconThemeEnum.SUCCESS_INNER;
    if (this.canValidate(row)) return IconThemeEnum.WARNING_INNER;
    if (this.maxNiveauUser === 0) return IconThemeEnum.SECONDARY_INNER;
    return IconThemeEnum.DEFAULT_INNER;
  }

  private canValidate(row: any): boolean {
    return (this.hasNiv1 && !row.validationNiv1Par)
      || (this.hasNiv2 && row.validationNiv1Par && !row.validationNiv2Par)
      || (this.hasNiv3 && row.validationNiv2Par && !row.validationNiv3Par);
  }

  public formatDateTitle(dateStr: string): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR', {day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'});
  }

  private formatMonth(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {year: 'numeric', month: 'long'});
  }

  ngOnDestroy(): void {
    this.utils.unsubscribe(this.sub);
  }

  getValidationTitle(row: any, niveau: number) {
    if (row[`validationNiv${niveau}Par`])
      return row[`validationNiv${niveau}Par`] + ' le ' + this.formatDateTitle(row[`dateValidationNiv${niveau}`]);
    else if (this[`hasNiv${niveau}`])
      return "En attente de validation";
    return "Non validé";
  }
}
